import { getYearLowestPriceTrackingProperties } from '@/domain/activity/black-friday-2026/utils/year-lowest-price';
import type { ICartGroup, ICartItem } from '@/api/types/cart';
import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { setBlockFlags } from '@/utils/feature-value';
import { genPrice, priceInfo } from '@/utils/price';
import { randomString } from '@/utils/tools';
import { useCallback, useMemo, useRef } from 'react';

/** 勾选且存在计价促销时用计价接口字段；基础币种固定为站点默认币种 */
function getCartItemProductPricesForSensors(
  userInfo: { is_member?: number; member_level?: number } | null | undefined,
  item: { product?: any },
  currencyName: string,
  priceContext?: { selected?: boolean; itemPromotion?: any },
) {
  const baseCurrencyName = env.NEXT_PUBLIC_DEFAULT_CURRENCY || '';
  const { product } = item as any;
  const curPriceInfo = priceInfo(userInfo, product, currencyName);
  const basePriceInfo = priceInfo(userInfo, product, baseCurrencyName);
  const promotion = priceContext?.itemPromotion;
  if (priceContext?.selected && promotion) {
    return {
      product_price: Number(promotion.product_original_price || 0),
      current_product_price: Number(promotion.product_price || 0),
      base_product_price: Number(promotion.base_product_original_price || 0),
      base_current_product_price: Number(promotion.base_product_price || 0),
    };
  }
  return {
    product_price: Number(curPriceInfo?.price || 0),
    current_product_price: Number(curPriceInfo?.sell_price || 0),
    base_product_price: Number(basePriceInfo?.price || 0),
    base_current_product_price: Number(basePriceInfo?.sell_price || 0),
  };
}

/** Cart_View cart_item_info_list：商品应支付金额（仅镜架商品段，基础币种） */
function getCartItemBaseProductActualAmount(
  item: ICartItem,
  promotion: any | undefined,
  groupSelected: boolean,
  lineProductPrices: { base_current_product_price: number },
): string {
  const qty = item.qty ?? 1;
  if (groupSelected && promotion) {
    const actual =
      Number(promotion.base_product_payable || 0) +
      Number(promotion.base_product_rule_discounts_price || 0);
    return actual.toFixed(2);
  }
  return (lineProductPrices.base_current_product_price * qty).toFixed(2);
}

function buildCartViewItemInfoEntry(
  item: ICartItem,
  groupSelected: boolean,
  userInfo: { is_member?: number; member_level?: number } | null | undefined,
  currencyName: string,
  getItemPromotion?: (cartId: number) => any,
): Record<string, string | number> {
  const baseCurrencyName = env.NEXT_PUBLIC_DEFAULT_CURRENCY || '';
  const product = item.product as any;
  const promotion = item.cart_id != null ? getItemPromotion?.(item.cart_id) : undefined;
  const lineProductPrices = getCartItemProductPricesForSensors(userInfo, item, currencyName, {
    selected: groupSelected,
    itemPromotion: promotion,
  });
  const base_product_price = String(lineProductPrices.base_product_price ?? '');
  const base_current_product_price = String(lineProductPrices.base_current_product_price ?? '');

  const isPendingLens = item.option?.is_pending_lens === 1;
  let lensPrice = {
    price: '0' as string | number,
    sell_price: '0' as string | number,
  };
  if (!isPendingLens && item.lens?.lens_type_price) {
    lensPrice = genPrice(item.lens.lens_type_price, baseCurrencyName);
  }
  const base_lens_price = String(lensPrice.price ?? '');
  const base_current_lens_price = String(lensPrice.sell_price ?? '');

  let prescriptionTypeName = '';
  let lensType = '';
  let lensTypeName = '';
  let lensId = '';
  let lensCode = '';
  if (!isPendingLens) {
    prescriptionTypeName = item.option?.prescription_type_name ?? '';
    lensType = item.lens?.lens_type ?? '';
    lensTypeName = item.lens?.lens_name ?? '';
    lensId = String(item.option?.lens_id ?? '');
    lensCode = item.lens?.lens_code ?? '';
  }

  const qty = item.qty ?? 1;
  const item_base_product_actual_amount = getCartItemBaseProductActualAmount(
    item,
    promotion,
    groupSelected,
    lineProductPrices,
  );

  const itemChecked = groupSelected && !item.disabled ? '勾选' : '未勾选';

  return {
    item_check_status: itemChecked,
    prescription_type_name: prescriptionTypeName,
    product_sku: product?.sku || '',
    shop_sku: product?.shop_sku || '',
    base_product_price,
    base_current_product_price,
    lens_type: lensType,
    lens_type_name: lensTypeName,
    lens_id: lensId,
    lens_code: lensCode,
    base_lens_price,
    base_current_lens_price,
    item_product_quantity: qty,
    item_base_product_actual_amount,
  };
}

interface CartViewItemInfoListItem {
  item_id: number;
  item_check_status: string | number;
  item_product_quantity: string | number;
  item_base_product_actual_amount: number;
}

interface CartViewProductInfoListItem {
  item_id: number;
  product_sku: string | number;
  shop_sku: string | number;
  base_product_price: number;
  base_current_product_price: number;
}

interface CartViewLensInfoListItem {
  item_id: number;
  prescription_type_name: string | number;
  lens_type: string | number;
  lens_id: string | number;
  lens_code: string | number;
  base_lens_price: number;
  base_current_lens_price: number;
}

/** Cart_View：按行拆成三个列表（顺序与购物车遍历一致） */
function buildCartViewCartItemInfoLists(
  rows: Array<ReturnType<typeof buildCartViewItemInfoEntry> & { item_id: number }>,
): {
  cart_item_info_list: CartViewItemInfoListItem[];
  cart_item_product_info_list: CartViewProductInfoListItem[];
  cart_item_lens_info_list: CartViewLensInfoListItem[];
} {
  const toNum = (v: string | number) => {
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isFinite(n) ? n : 0;
  };

  const cartItemInfoList: CartViewItemInfoListItem[] = [];
  const cartItemProductInfoList: CartViewProductInfoListItem[] = [];
  const cartItemLensInfoList: CartViewLensInfoListItem[] = [];

  for (const row of rows) {
    cartItemInfoList.push({
      item_id: row.item_id,
      item_check_status: row.item_check_status,
      item_product_quantity: row.item_product_quantity,
      item_base_product_actual_amount: toNum(row.item_base_product_actual_amount),
    });
    cartItemProductInfoList.push({
      item_id: row.item_id,
      product_sku: row.product_sku,
      shop_sku: row.shop_sku,
      base_product_price: toNum(row.base_product_price),
      base_current_product_price: toNum(row.base_current_product_price),
    });
    cartItemLensInfoList.push({
      item_id: row.item_id,
      prescription_type_name: row.prescription_type_name,
      lens_type: row.lens_type,
      lens_id: row.lens_id,
      lens_code: row.lens_code,
      base_lens_price: toNum(row.base_lens_price),
      base_current_lens_price: toNum(row.base_current_lens_price),
    });
  }

  return {
    cart_item_info_list: cartItemInfoList,
    cart_item_product_info_list: cartItemProductInfoList,
    cart_item_lens_info_list: cartItemLensInfoList,
  };
}

/** Cart_View 行级明细（数组）；此函数仍可用于单测或其它组装 */
export function buildCartViewCartItemInfo(
  cartBuyGroups: ICartGroup[] | undefined | null,
  userInfo: { is_member?: number; member_level?: number } | null | undefined,
  options?: {
    currencyName?: string;
    getItemPromotion?: (cartId: number) => any;
  },
): Array<ReturnType<typeof buildCartViewItemInfoEntry> & { item_id: number }> {
  const currencyName = options?.currencyName || '';
  const getItemPromotion = options?.getItemPromotion;
  const list: Array<ReturnType<typeof buildCartViewItemInfoEntry> & { item_id: number }> = [];
  if (!cartBuyGroups?.length) {
    return list;
  }
  for (const group of cartBuyGroups) {
    const groupSelected = !!group.selected;
    const carts = group.carts || [];
    for (const cart of carts) {
      const id = cart.cart_id;
      if (id == null) continue;
      list.push({
        item_id: id,
        ...buildCartViewItemInfoEntry(
          cart,
          groupSelected,
          userInfo,
          currencyName,
          getItemPromotion,
        ),
      });
    }
  }
  return list;
}

/** 与 useCartState.allQty 一致：可购分组内全部行 qty 之和（与页面列表数据源一致） */
function sumQtyInCartGroups(groups: ICartGroup[] | undefined | null): number {
  if (!groups?.length) return 0;
  return groups.reduce((total, group) => {
    const { carts } = group;
    if (!carts?.length) return total;
    return total + carts.reduce((sub, cart) => sub + (cart.qty || 0), 0);
  }, 0);
}

interface CartItem {
  option?: ICartItem['option'];
  cart_id: number;
  product_id: number;
  shop_sku: string;
  business_sku: string;
  product_sku: string;
  product_name: string;
  qty: number;
  product?: {
    products_category: string;
    shop_sku: string;
    business_sku: string;
    sku: string;
    short_name: string;
    id: number;
  };
}

interface CartBannerTrackingParams {
  jump_type?: number | string;
  Jump_target?: string;
  people_name?: string;
}

interface PriceData {
  total: string;
  subtotal: string;
  discount: string;
  tmp?: {
    carts?: Array<{
      buried_point_data?: {
        campaign_type_id_list?: number[];
        campaign_sku_list?: string[];
      };
    }>;
  };
}

// 推荐商品相关接口
interface RecommendProduct {
  correlation: any;
  id?: number;
  shop_sku?: string;
  business_sku?: string;
  name?: string;
  short_name?: string;
  price_info?: Array<{
    currency?: string;
    price?: string;
    sell_price?: string;
  }>;
  recall_group_type?: string;
  fine_ranking_type?: string;
  recommend_result_type?: string;
  product_display_label?: string[];
  product_collect_num?: number;
  product_rating?: number;
  first_picture_url?: string;
  display_color_num?: number;
  is_glass?: number;
}

interface CartRecommendTrackingParams {
  product_index: number;
  shop_sku: string;
  business_sku: string;
  product_sku: string;
  product_id: string;
  product_price: string;
  current_product_price: string;
  base_current_product_price: string;
  currency: string;
  recall_group_type: string;
  fine_ranking_type: string;
  recommend_result_type: string;
  product_display_label: string[];
  product_collect_num: number;
  product_rating: number;
  display_color_num: number;
  first_picture_url: string;
  product_class_name: string;
  module_name?: string;
  /** 埋点表定义的展示场景，与推荐算法或活动折扣比例无关。 */
  recommend_scene?: '普通推荐' | '第二件优惠提示样式';
}

export function useCartSensorsTracking() {
  const { trackEvent } = useReporting();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const canBuyList = useStore((state) => state.canBuyList);
  const token = useStore((state) => state.token);
  const visitorId = useStore((state) => state.visitorId);
  const { getFromRouterInfo } = useRouteInfo();

  // 记录页面访问状态
  const isCartViewSentRef = useRef(false);
  /** Cart_Leave.view_duration：进页起算，勿随延迟 Cart_View 重置 */
  const pageStartTimeRef = useRef<number>(Date.now());

  // 在Hook内部生成购物车UUID（每次页面访问唯一）
  const cartViewUuid = useMemo(() => generateCartViewUuid(), []);

  /**
   * 生成TikTok事件ID
   * - 有token时：userInfo.id
   * - 无token时：visitorId
   */
  const generateTtqEventId = useCallback(
    (shopSku?: string) => {
      const baseId = token ? userInfo.id : visitorId;
      return shopSku ? `${baseId}${shopSku}` : baseId || '';
    },
    [token, visitorId],
  );

  // 在Hook内部获取进入场景
  const entranceCartScene = useMemo(() => {
    if (typeof window !== 'undefined') {
      // 从sessionStorage获取进入场景，获取后立即删除
      const cachedScene = sessionStorage.getItem('zl-sensors-entrance-cart-scene');
      if (cachedScene) {
        sessionStorage.removeItem('zl-sensors-entrance-cart-scene');
        return cachedScene;
      }
    }
    return '其它'; // 默认场景
  }, []);

  /**
   * 提取活动相关数据
   */
  const extractCampaignData = useCallback((priceData: PriceData) => {
    const campaign_type_id_list: number[] = [];
    const campaign_sku_list: string[] = [];

    const carts = priceData?.tmp?.carts || [];
    carts.forEach((item) => {
      if (item.buried_point_data?.campaign_type_id_list?.length) {
        campaign_type_id_list.push(...item.buried_point_data.campaign_type_id_list);
      }
      if (item.buried_point_data?.campaign_sku_list?.length) {
        campaign_sku_list.push(...item.buried_point_data.campaign_sku_list);
      }
    });

    return {
      campaign_type_id_list: Array.from(new Set(campaign_type_id_list)),
      campaign_sku_list: Array.from(new Set(campaign_sku_list)),
    };
  }, []);

  /**
   * 构建购物车推荐商品神策埋点数据
   */
  const getCartRecommendSensorsData = useCallback(
    (params: CartRecommendTrackingParams, item: RecommendProduct) => {
      // 收集购物车中商品的shop_sku和product_id列表
      const cart_item_shop_sku_list: string[] = [];
      const cart_item_product_id_list: number[] = [];

      canBuyList.forEach((cartGroup) => {
        cartGroup.carts?.forEach((cart) => {
          if (cart.product?.shop_sku) {
            cart_item_shop_sku_list.push(cart.product.shop_sku);
          }
          if (cart.product_id) {
            cart_item_product_id_list.push(cart.product_id);
          }
        });
      });

      const eventParams = {
        cart_view_uuid: cartViewUuid,
        product_index: params.product_index,
        shop_sku: params.shop_sku,
        business_sku: params.business_sku,
        product_sku: params.product_sku,
        product_id: params.product_id,
        product_name: item.short_name || '',
        entrance_cart_scene: entranceCartScene,
        product_price: Number(params.product_price),
        current_product_price: Number(params.current_product_price),
        base_current_product_price: Number(params.base_current_product_price),
        currency: params.currency,
        recall_group_type: params.recall_group_type,
        fine_ranking_type: params.fine_ranking_type,
        recommend_result_type: params.recommend_result_type || '',
        product_display_label: params.product_display_label,
        ...getYearLowestPriceTrackingProperties(item),
        product_collect_num: params.product_collect_num,
        product_rating: params.product_rating,
        recommend_position_type: 'cart_recommend',
        display_color_num: params.display_color_num,
        block_flags: 'cart@recommend_0@',
        block_flags_ext: '',
        dollar_exchange_rate: parseFloat(currency?.exchange_rate || '1'),
        first_picture_url: params.first_picture_url,
        cart_item_shop_sku_list,
        cart_item_product_id_list,
        product_class_name: params.product_class_name,
        ...(params.module_name ? { module_name: params.module_name } : {}),
      };

      return eventParams;
    },
    [canBuyList, currency, cartViewUuid, entranceCartScene],
  );

  /**
   * 神策埋点：购物车页面浏览
   * @param checkedProductQuantity 勾选商品件数，须与 CartRight 中 selectedItems 汇总 qty（totalItems）一致
   * @param cartGroupsForView 与首屏渲染一致的待结算、待配镜分组；待配镜分组固定按未勾选处理
   * @param baseCheckedProductAmount 计价接口 base_checked_product_amount（基础币种，上报为 number）
   * @param baseCheckedSubtotalProductAmount 计价接口 base_checked_subtotal_product_amount（同上）
   */
  const trackCartView = useCallback(
    (
      checkedProductQuantity: number,
      getItemPromotion: ((cartId: number) => any) | undefined,
      cartGroupsForView: ICartGroup[],
      baseCheckedProductAmount: string | number | undefined,
      baseCheckedSubtotalProductAmount: string | number | undefined,
    ) => {
      // 防止重复发送
      if (isCartViewSentRef.current) return;
      if (!cartViewUuid) return;

      isCartViewSentRef.current = true;
      const { refer_page_name, refer_url_path } = getFromRouterInfo();
      const currencyName = currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY;
      try {
        const cartItemRows = buildCartViewCartItemInfo(cartGroupsForView, userInfo, {
          currencyName,
          getItemPromotion,
        });
        const { cart_item_info_list, cart_item_product_info_list, cart_item_lens_info_list } =
          buildCartViewCartItemInfoLists(cartItemRows);
        const product_quantity = sumQtyInCartGroups(cartGroupsForView);
        // Cart_View 参数：包含 product_quantity、checked_product_quantity，不包含 view_duration
        const eventParams = {
          cart_view_uuid: cartViewUuid,
          entrance_cart_scene: entranceCartScene,
          refer_page_name,
          refer_url_path,
          product_quantity,
          checked_product_quantity: checkedProductQuantity,
          base_checked_product_amount: Number(baseCheckedProductAmount) || 0,
          base_checked_subtotal_product_amount: Number(baseCheckedSubtotalProductAmount) || 0,
          dollar_exchange_rate: parseFloat(currency?.exchange_rate || '1'),
          currency: currencyName,
          cart_item_info_list,
          cart_item_product_info_list,
          cart_item_lens_info_list,
        };

        trackEvent(
          {
            eventName: 'Cart_View',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('Cart_View tracking error:', e);
      }
    },
    [trackEvent, cartViewUuid, entranceCartScene, userInfo, currency, getFromRouterInfo],
  );

  /**
   * 神策埋点：离开购物车页面
   */
  const trackCartLeave = useCallback(
    (_fromRouterInfo: any) => {
      // 如果没有发送过页面浏览事件，则不发送离开事件
      if (!isCartViewSentRef.current) return;

      isCartViewSentRef.current = false;

      try {
        const viewDuration = Math.ceil((Date.now() - pageStartTimeRef.current) / 1000);
        const { refer_page_name, refer_url_path } = _fromRouterInfo;

        // Cart_Leave 参数：不包含 product_quantity，只包含 view_duration
        const eventParams = {
          cart_view_uuid: cartViewUuid,
          entrance_cart_scene: entranceCartScene,
          refer_page_name,
          refer_url_path,
          view_duration: viewDuration,
        };

        trackEvent(
          {
            eventName: 'Cart_Leave',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('Cart_Leave tracking error:', e);
      }
    },
    [trackEvent, cartViewUuid, entranceCartScene],
  );

  // const trackThirdPartyCheckoutClick = useCallback(() => {
  //   try {
  //     const eventParams = {
  //       cart_view_uuid: cartViewUuid,
  //       entrance_cart_scene: entranceCartScene,
  //     };
  // }, [trackEvent, cartViewUuid, entranceCartScene]);
  /**
   * 神策埋点：结算按钮点击
   * @param selectedItems 选中的商品列表
   * @param priceData 价格数据
   */
  const trackCheckoutClickForThirdParty = useCallback(
    (selectedItems: CartItem[], priceData: PriceData) => {
      try {
        trackEvent(
          undefined,
          [
            ProviderType.SENSORS,
            ProviderType.TIKTOK,
            ProviderType.APPLOVIN,
            ProviderType.GA4,
            ProviderType.RTB_HOUSE,
          ],
          {
            cartItems: selectedItems,
            priceData,
            currency,
            ttqEventId: generateTtqEventId(),
            cartViewUuid,
            entranceCartScene,
          },
          'checkout.click',
        );
      } catch (e) {
        logger.error('CartCheckOut_Click tracking error:', e);
      }
    },
    [trackEvent, extractCampaignData, currency, cartViewUuid, entranceCartScene],
  );

  /**
   * 神策埋点：结算结果
   * @param selectedItems 选中的商品列表
   * @param priceData 价格数据
   * @param isCheckoutSuccess 是否结算成功
   * @param serviceCode 服务状态码
   */
  const trackCheckoutResult = useCallback(
    (
      selectedItems: CartItem[],
      priceData: PriceData,
      isCheckoutSuccess: boolean,
      serviceCode: string,
      msg: string,
    ) => {
      try {
        trackEvent(
          undefined,
          [
            ProviderType.SENSORS,
            ProviderType.SNAPCHAT,
            ProviderType.SNAPCHAT_CONVERSION,
            ProviderType.OPENAI,
          ],
          {
            cartItems: selectedItems,
            priceData,
            currency,
            cartViewUuid,
            entranceCartScene,
            isCheckoutSuccess,
            serviceCode,
            msg,
          },
          'checkout.result',
        );
      } catch (e) {
        logger.error('CartCheckOut_Result tracking error:', e);
      }
    },
    [trackEvent, extractCampaignData, currency, cartViewUuid, entranceCartScene],
  );

  /**
   * 神策埋点：结算商品详细结果
   * @param item 商品信息
   * @param isCheckoutSuccess 是否结算成功
   * @param serviceCode 服务状态码
   * @param productAmount 商品金额
   */
  const trackCheckoutProductDetailResult = useCallback(
    (
      item: CartItem,
      isCheckoutSuccess: boolean,
      serviceCode: string,
      productAmount = 0,
      msg = '',
      priceContext: { selected?: boolean; itemPromotion?: any } = { selected: true },
    ) => {
      try {
        const currencyName = currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY;
        const {
          product_price,
          current_product_price,
          base_product_price,
          base_current_product_price,
        } = getCartItemProductPricesForSensors(userInfo, item, currencyName, priceContext);

        // 提取各种价格信息
        const coatingPrice = (item as any).coating?.price_info
          ? genPrice((item as any).coating.price_info, currencyName)
          : {
              price: '0',
              sell_price: '0',
            };
        const lensPrice = (item as any).lens?.lens_type_price
          ? genPrice((item as any).lens.lens_type_price, currencyName)
          : {
              price: '0',
              sell_price: '0',
            };
        const indexPrice = (item as any).lens?.index_price
          ? genPrice((item as any).lens.index_price, currencyName)
          : {
              price: '0',
              sell_price: '0',
            };
        const upgradeBeforeLensPrice = (item as any).option?.old_lens_price
          ? genPrice((item as any).option.old_lens_price, currencyName)
          : {
              price: '0',
              sell_price: '0',
            };

        const eventParams = {
          cart_view_uuid: cartViewUuid,
          entrance_cart_scene: entranceCartScene,
          shop_sku: item.product?.shop_sku || '',
          business_sku: item.product?.business_sku || '',
          product_sku: item.product?.sku || '',
          product_id: String(item.product_id || ''),
          product_name: item.product?.short_name || '',
          product_class_name: item.product?.products_category || '',
          product_price,
          base_product_price,
          current_product_price,
          base_current_product_price,
          product_quantity: item.qty || 0,
          product_amount: productAmount,
          dollar_exchange_rate: parseFloat(currency?.exchange_rate || '1'),
          currency: currencyName,
          is_checkout_success: isCheckoutSuccess,
          service_code: serviceCode,
          // 处方相关信息
          prescription_type: (item as any).option?.prescription_type || '',
          prescription_type_name: (item as any).option?.prescription_type_name || '',
          coating_id: `${(item as any).option?.coating_id}` || '',
          coating_type_name: (item as any).coating?.coating_name || '',
          coating_price: Number(coatingPrice?.price || '0'),
          current_coating_price: Number(coatingPrice?.sell_price || '0'),
          lens_id: `${(item as any).option?.lens_id}` || '',
          lens_type_name: (item as any).lens?.lens_name || '',
          lens_type: (item as any).lens?.lens_type || '',
          lens_code: (item as any).lens?.lens_code || '',
          lens_color: (item as any).lens?.lens_color || '',
          lens_brand: (item as any).lens?.brand || '',
          lens_brand_name: (item as any).lens?.brand_name || '',
          lens_price: Number(lensPrice?.price || '0'),
          current_lens_price: Number(lensPrice?.sell_price || '0'),
          upgrade_before_lens_price: Number(upgradeBeforeLensPrice?.sell_price || '0'),
          lens_index: `${(item as any).lens?.refractive_index}`,
          lens_index_name: (item as any).lens?.lens_index || '',
          lens_index_price: Number(indexPrice?.price || '0'),
          current_lens_index_price: Number(indexPrice?.sell_price || '0'),
          msg,
          ...item.option?.buried_datas,
        };

        trackEvent(
          {
            eventName: 'CartCheckOutProductDetail_Result',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('CartCheckOutProductDetail_Result tracking error:', e);
      }
    },
    [trackEvent, currency, userInfo, cartViewUuid, entranceCartScene],
  );

  /**
   * 神策埋点：购物车页你也喜欢坑位点击
   * @TrackType sensorsdata
   * @EventType CartPageYouAlsoLikeMkt_Click
   * @EventDesc 购物车页你也喜欢坑位点击
   * @param params 追踪参数
   * @param item 商品信息
   */
  const trackCartPageYouAlsoLikeMktClick = useCallback(
    (params: CartRecommendTrackingParams, item: RecommendProduct) => {
      try {
        const eventName = 'CartPageYouAlsoLikeMkt_Click';
        const eventParams = {
          ...getCartRecommendSensorsData(params, item),
          recommend_scene: params.recommend_scene || '普通推荐',
        };

        // 设置block_flags相关信息
        setBlockFlags({
          block_flags: eventParams.block_flags,
          block_flags_ext: eventParams.block_flags_ext,
          fine_ranking_type: eventParams.fine_ranking_type,
          recall_group_type: eventParams.recall_group_type,
          recommend_result_type: eventParams.recommend_result_type || '',
          product_index: eventParams.product_index,
          first_picture_url: eventParams.first_picture_url,
          cart_item_shop_sku_list: eventParams.cart_item_shop_sku_list,
          cart_item_product_id_list: eventParams.cart_item_product_id_list.map(String),
          refer_page_position: '购物车你也喜欢推荐商品',
        });

        trackEvent(
          {
            eventName,
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('CartPageYouAlsoLikeMkt_Click tracking error:', error);
      }
    },
    [trackEvent, getCartRecommendSensorsData],
  );

  /**
   * 神策埋点：购物车页你也喜欢坑位曝光
   * @TrackType sensorsdata
   * @EventType CartPageYouAlsoLikeMkt_Exp
   * @EventDesc 购物车页你也喜欢坑位曝光
   * @param params 追踪参数
   * @param item 商品信息
   */
  const trackCartPageYouAlsoLikeMktExp = useCallback(
    (params: CartRecommendTrackingParams, item: RecommendProduct) => {
      try {
        const eventName = 'CartPageYouAlsoLikeMkt_Exp';
        const eventParams = getCartRecommendSensorsData(params, item);

        trackEvent(
          {
            eventName,
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('CartPageYouAlsoLikeMkt_Exp tracking error:', error);
      }
    },
    [trackEvent, getCartRecommendSensorsData],
  );
  const trackCartPageYouAlsoLikeMktValidExp = useCallback(
    (params: CartRecommendTrackingParams, item: RecommendProduct) => {
      try {
        const eventName = 'CartPageLikeValidMkt_Exp';
        const eventParams = {
          ...getCartRecommendSensorsData(params, item),
          recommend_scene: params.recommend_scene || '普通推荐',
        };

        trackEvent(
          {
            eventName,
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('CartPageLikeValidMkt_Exp tracking error:', error);
      }
    },
    [trackEvent, getCartRecommendSensorsData],
  );

  /**
   * 神策埋点：购物车页你也喜欢模块曝光
   * @TrackType sensorsdata
   * @EventType CartPageYouAlsoLikeModel_Exp
   * @EventDesc 购物车页你也喜欢模块曝光
   * @param params 包含entrance_cart_scene的参数
   */
  const trackCartPageYouAlsoLikeModelExp = useCallback(() => {
    try {
      const eventName = 'CartPageYouAlsoLikeModel_Exp';

      trackEvent(
        {
          eventName,
          properties: {
            entrance_cart_scene: entranceCartScene,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('CartPageYouAlsoLikeModel_Exp tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * 神策埋点：购物车商品按钮点击
   * @param item 商品信息
   * @param btnName 按钮名称
   */
  const trackCartItemBtnClick = useCallback(
    (
      item: CartItem,
      btnName: string,
      priceContext?: { selected?: boolean; itemPromotion?: any },
    ) => {
      try {
        const currencyName = currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY;
        const {
          product_price,
          current_product_price,
          base_product_price,
          base_current_product_price,
        } = getCartItemProductPricesForSensors(userInfo, item, currencyName, priceContext);

        const cartItemRow = item as unknown as ICartItem;
        const rowSelected = priceContext?.selected ?? cartItemRow.selected ?? false;
        const item_check_status = rowSelected && !cartItemRow.disabled ? '勾选' : '未勾选';

        const eventParams = {
          cart_view_uuid: cartViewUuid,
          entrance_cart_scene: entranceCartScene,
          item_check_status,
          business_sku: item.product?.business_sku || '',
          shop_sku: item.product?.shop_sku || '',
          product_sku: item.product?.sku || '',
          product_id: String(item.product_id || item.product?.id || ''),
          product_name: item.product?.short_name || '',
          btn_name: btnName,
          product_class_name: item.product?.products_category || '',
          product_price,
          base_product_price,
          current_product_price,
          base_current_product_price,
          currency: currencyName,
          dollar_exchange_rate: parseFloat(currency?.exchange_rate || '1'),
          ...item.option?.buried_datas,
        };

        trackEvent(
          {
            eventName: 'CartitemBtn_Click',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('CartitemBtn_Click tracking error:', e);
      }
    },
    [trackEvent, cartViewUuid, entranceCartScene, currency, userInfo],
  );

  /**
   * 神策埋点：购物车处方编辑按钮点击
   * @param item 商品信息
   * @param btnName 按钮名称
   */
  const trackCartPrescriptionEditBtnClick = useCallback(
    (item: CartItem, btnName: string) => {
      try {
        const eventParams = {
          cart_view_uuid: cartViewUuid,
          entrance_cart_scene: entranceCartScene,
          business_sku: item.product?.business_sku || '',
          shop_sku: item.product?.shop_sku || '',
          product_sku: item.product?.sku || '',
          product_id: String(item.product_id || item.product?.id || ''),
          product_name: item.product?.short_name || '',
          btn_name: btnName,
          product_class_name: item.product?.products_category || '',
        };

        trackEvent(
          {
            eventName: 'CartPrescriptionEditBtn_Click',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('CartPrescriptionEditBtn_Click tracking error:', e);
      }
    },
    [trackEvent, cartViewUuid, entranceCartScene],
  );

  // 收藏按钮点击埋点
  const trackCollectBtnClick = useCallback(
    (item: CartItem, isBatchAction = false) => {
      try {
        // 区分批量收藏和单个商品收藏的逻辑
        let collectType: string;
        if (isBatchAction) {
          // 批量收藏时，统一使用"收藏"（用户的批量操作意图）
          collectType = '收藏';
        } else {
          // 单个商品收藏时，根据收藏状态动态设置
          // is_wish = 1: 已收藏，点击后是"取消收藏"
          // is_wish = 2: 未收藏，点击后是"收藏"
          collectType = (item.product as any)?.is_wish === 2 ? '收藏' : '取消收藏';
        }
        const { refer_page_name } = getFromRouterInfo();
        const eventParams = {
          entrance_cart_scene: entranceCartScene,
          page_name: refer_page_name, // TODO: 来源页面
          url_path: window.location.href,
          collect_type: collectType,
          virtually_no: userInfo?.uuid ? `${userInfo.uuid}${Date.now()}` : `null${Date.now()}`,
          shop_sku: item.product?.shop_sku || '',
          business_sku: item.product?.business_sku || '',
          product_sku: item.product?.sku || '',
          product_id: String(item.product_id || item.product?.id || ''),
          product_name: item.product?.short_name || '',
        };

        trackEvent(
          {
            eventName: 'CollectBtn_Click',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('CollectBtn_Click tracking error:', e);
      }
    },
    [trackEvent, entranceCartScene, userInfo],
  );

  // 收藏点击埋点
  const trackCollectClick = useCallback(
    (item: CartItem) => {
      try {
        const { refer_page_name } = getFromRouterInfo();
        const eventParams = {
          page_name: refer_page_name,
          url_path: window.location.href,
          virtually_no: userInfo?.uuid ? `${userInfo.uuid}${Date.now()}` : `null${Date.now()}`,
          shop_sku: item.product?.shop_sku || '',
          business_sku: item.product?.business_sku || '',
          product_sku: item.product?.sku || '',
          product_id: String(item.product_id || item.product?.id || ''),
          product_name: item.product?.short_name || '',
          collect_scene: '购物车收藏',
        };

        trackEvent(
          {
            eventName: 'Collect_Click',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('Collect_Click tracking error:', e);
      }
    },
    [trackEvent, userInfo],
  );

  /**
   * 神策埋点：购物车功能按钮点击
   * @param btnName 按钮名称
   */
  const trackCartFunctionBtnClick = useCallback(
    (btnName: string) => {
      try {
        const eventParams = {
          cart_view_uuid: cartViewUuid,
          entrance_cart_scene: entranceCartScene,
          btn_name: btnName,
        };

        trackEvent(
          {
            eventName: 'CartFunctionBtn_Click',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('CartFunctionBtn_Click tracking error:', e);
      }
    },
    [trackEvent, cartViewUuid, entranceCartScene],
  );

  // 镜片升级点击埋点
  const trackLensUpgradeClick = useCallback(
    (item: CartItem, upgradeType: string, upgradeLensCode: string) => {
      try {
        const eventParams = {
          shop_sku: item.product?.shop_sku || '',
          business_sku: item.product?.business_sku || '',
          product_sku: item.product?.sku || '',
          product_id: String(item.product_id || item.product?.id || ''),
          product_name: item.product?.short_name || '',
          upgrade_type: upgradeType,
          upgrade_lens_code: upgradeLensCode,
          product_class_name: item.product?.products_category || '',
        };

        trackEvent(
          {
            eventName: 'LensUpgrade_Click',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('LensUpgrade_Click tracking error:', e);
      }
    },
    [trackEvent],
  );

  // 镜片升级展示埋点
  const trackLensUpgradeView = useCallback(
    (item: CartItem, upgradeType: string, upgradeLensCode: string) => {
      try {
        const eventParams = {
          shop_sku: item.product?.shop_sku || '',
          business_sku: item.product?.business_sku || '',
          product_sku: item.product?.sku || '',
          product_id: String(item.product_id || item.product?.id || ''),
          product_name: item.product?.short_name || '',
          upgrade_type: upgradeType,
          upgrade_lens_code: upgradeLensCode,
          product_class_name: item.product?.products_category || '',
        };

        trackEvent(
          {
            eventName: 'LensUpgrade_View',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('LensUpgrade_View tracking error:', e);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：购物车banner点击
   * @param advertObj 广告对象
   */
  const trackCartPageBannerClick = useCallback(
    (advertObj: CartBannerTrackingParams | false) => {
      try {
        const banner = advertObj || undefined;
        const eventParams = {
          cart_view_uuid: cartViewUuid,
          entrance_cart_scene: entranceCartScene,
          jump_type: String(banner?.jump_type || ''),
          jump_content: banner?.Jump_target || '',
          people_name: typeof banner?.people_name === 'string' ? banner.people_name : '',
        };

        trackEvent(
          {
            eventName: 'CartPageBanner_Click',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('CartPageBanner_Click tracking error:', e);
      }
    },
    [trackEvent, cartViewUuid, entranceCartScene],
  );

  /**
   * 神策埋点：购物车banner曝光
   * @param advertObj 广告对象
   */
  const trackCartPageBannerExp = useCallback(
    (advertObj: CartBannerTrackingParams | false) => {
      try {
        const banner = advertObj || undefined;
        const eventParams = {
          cart_view_uuid: cartViewUuid,
          entrance_cart_scene: entranceCartScene,
          jump_type: String(banner?.jump_type || ''),
          jump_content: banner?.Jump_target || '',
          people_name: typeof banner?.people_name === 'string' ? banner.people_name : '',
        };

        trackEvent(
          {
            eventName: 'CartPageBanner_Exp',
            properties: eventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('CartPageBanner_Exp tracking error:', e);
      }
    },
    [trackEvent, cartViewUuid, entranceCartScene],
  );

  return {
    // 核心埋点方法
    trackCartView,
    trackCartLeave,
    trackCheckoutClickForThirdParty,
    trackCheckoutResult,
    trackCheckoutProductDetailResult,
    trackCartItemBtnClick,
    trackCartPrescriptionEditBtnClick,
    trackCollectClick,
    trackCartFunctionBtnClick,
    trackCollectBtnClick,
    trackLensUpgradeClick,
    trackLensUpgradeView,

    // 广告位埋点方法
    trackCartPageBannerClick,
    trackCartPageBannerExp,

    // 推荐商品埋点方法
    trackCartPageYouAlsoLikeMktClick,
    trackCartPageYouAlsoLikeMktExp,
    trackCartPageYouAlsoLikeMktValidExp,
    trackCartPageYouAlsoLikeModelExp,

    // 工具方法
    extractCampaignData,
    getCartRecommendSensorsData,

    // 状态管理
    resetCartViewState: () => {
      isCartViewSentRef.current = false;
    },

    // 获取当前购物车UUID和进入场景
    cartViewUuid,
    entranceCartScene,
  };
}

/**
 * 生成购物车UUID
 */
export const generateCartViewUuid = () => {
  return `cart_${randomString()}`;
};

// 导出类型
export type { CartItem, CartRecommendTrackingParams, PriceData, RecommendProduct };
